import React, { useCallback } from 'react';

import dayjs from '~/timeInit';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Button, Chip, IconButton, Link } from '@mui/material';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';

import {
  CERTIFICATE_STATUS_COLOR,
  CERTIFICATE_TYPE_LABELS,
  PROCESS_STATUS_COLOR,
} from '~/components/Users/Orders/Atol/atolConstants';

import { useGetAtolCertificatesByCustomer } from '~/hooks/apis/travel-compliance/useGetAtolCertificatesByCustomer';

import { AtolCertificatesByCustomerItem } from '~/services/TravelComplianceService';

const columns: Array<GridColDef<AtolCertificatesByCustomerItem>> = [
  {
    field: 'uniqueReferenceNumber',
    headerName: 'Reference Number',
    flex: 1.2,
    display: 'flex',
  },
  {
    field: 'version',
    headerName: 'Version',
    width: 80,
    display: 'flex',
  },
  {
    field: 'coveredPrice',
    headerName: 'Covered Price',
    flex: 1,
    display: 'flex',
    valueGetter: (_value, row) => `${row.currency} ${row.coveredPrice}`,
  },
  {
    field: 'certificateStatus',
    headerName: 'Certificate Status',
    flex: 1,
    display: 'flex',
    renderCell: (params) => (
      <Chip label={params.value} size="small" color={CERTIFICATE_STATUS_COLOR[params.value] ?? 'default'} />
    ),
  },
  {
    field: 'processStatus',
    headerName: 'Process Status',
    flex: 1,
    display: 'flex',
    renderCell: (params) => (
      <Chip label={params.value} size="small" color={PROCESS_STATUS_COLOR[params.value] ?? 'default'} />
    ),
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    flex: 1,
    display: 'flex',
    valueGetter: (_value, row) => dayjs(row.createdAt).format('DD MMM YYYY HH:mm'),
  },
  {
    field: 'earliestDepartureDate',
    headerName: 'Departure Date',
    flex: 1,
    display: 'flex',
    valueGetter: (_value, row) =>
      row.earliestDepartureDate ? dayjs(row.earliestDepartureDate).format('DD MMM YYYY') : '-',
  },
  {
    field: 'certificateType',
    headerName: 'Certificate Type',
    flex: 1,
    display: 'flex',
    valueGetter: (_value, row) => CERTIFICATE_TYPE_LABELS[row.certificateType] ?? row.certificateType,
  },
  {
    field: 'actions',
    headerName: '',
    width: 60,
    sortable: false,
    display: 'flex',
    renderCell: (params) => (
      <IconButton
        component={Link}
        href={`/atol-certificate/${params.row.id}`}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
      >
        <OpenInNewIcon fontSize="small" />
      </IconButton>
    ),
  },
];

type Props = {
  customerId: string;
};

export default function AtolCertificatesTab({ customerId }: Props) {
  const { data: certificates = [], isLoading, error, refetch } = useGetAtolCertificatesByCustomer(customerId);

  const handleRowClick = useCallback((params: GridRowParams<AtolCertificatesByCustomerItem>) => {
    window.open(`/atol-certificate/${params.row.id}`, '_blank', 'noopener,noreferrer');
  }, []);

  return (
    <>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" variant="outlined" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {error.message || 'Failed to load ATOL certificates'}
        </Alert>
      )}

      <DataGrid
        loading={isLoading}
        columns={columns}
        rows={certificates}
        getRowId={(row) => row.id}
        onRowClick={handleRowClick}
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        disableRowSelectionOnClick
        autoHeight
        sx={{
          '& .MuiDataGrid-row': { cursor: 'pointer' },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
        }}
      />
    </>
  );
}
